"use client";

import * as React from "react";

import AuthPaywall from "@/components/shared/auth-paywall/auth-paywall";
import { User } from "@/lib/types/auth";
import { useAuth } from "@/stores/use-auth";

export function withAuthGate<P extends { user: User }>(
  WrappedComponent: React.ComponentType<P>,
  {
    customLoader,
    customPaywall,
    testId,
  }: {
    customLoader?: React.ReactNode;
    customPaywall?:
      | React.ReactNode
      | ((props: Omit<P, "user">) => React.ReactNode);
    testId?: string;
  },
) {
  const ComponentWithAuth: React.FC<Omit<P, "user">> = (props) => {
    const { user, isLoading } = useAuth();

    if (isLoading) {
      return customLoader !== undefined ? (
        customLoader
      ) : (
        <AuthPaywall isLoading testId={testId} />
      );
    }

    if (user) {
      return <WrappedComponent {...(props as P)} user={user} />;
    } else {
      if (customPaywall !== undefined) {
        return typeof customPaywall === "function"
          ? customPaywall(props)
          : customPaywall;
      }
      return <AuthPaywall testId={testId} />;
    }
  };

  ComponentWithAuth.displayName = `AuthenticatedGate(${
    WrappedComponent.displayName || WrappedComponent.name || "Component"
  })`;

  return ComponentWithAuth;
}
